import './RichEditor.scss';

import { Box, Button, Flex, Stack } from '@chakra-ui/react';
import {
  ListBullets,
  ListNumbers,
  TextB,
  TextHOne,
  TextHThree,
  TextHTwo,
  TextItalic,
  TextStrikethrough,
  TextUnderline
} from '@phosphor-icons/react';
import { useCallback, useEffect, memo } from 'react';
import { Editor, EditorContent, useEditor, useEditorState } from '@tiptap/react';

import { Color } from '@tiptap/extension-color';
import Document from '@tiptap/extension-document';
import ListItem from '@tiptap/extension-list-item';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import Text from '@tiptap/extension-text';
import { TextStyle } from '@tiptap/extension-text-style';

type EditorType = 'candidate-details-note' | 'interview-note' | 'job-description';

interface RichEditorProps {
  content: string;
  placeholder?: string;
  onChange?: (content: string) => void;
  readOnly?: boolean;
  type?: EditorType;
  className?: string;
  'aria-label'?: string;
}

interface EditorState {
  isHeading1: boolean;
  isHeading2: boolean;
  isHeading3: boolean;
  isBold: boolean;
  isItalic: boolean;
  isStrike: boolean;
  isUnderline: boolean;
  isBulletList: boolean;
  isOrderedList: boolean;
}

interface MenuBarProps {
  editor: Editor;
  editorState: EditorState;
}

const convertNewlinesToHtml = (text: string): string => {
  return text.replace(/\n/g, '<br>');
};

const MenuBar = memo(({ editor, editorState }: MenuBarProps) => {
  if (!editor) {
    return null;
  }

  const buttonProps = {
    variant: 'ghost' as const,
    color: 'neutrals.900',
    w: '40px',
    h: '40px',
    p: '0',
    _hover: { bg: 'primary.100' },
    borderRadius: '8px'
  };

  return (
    <Box
      w="100%"
      p="8px 16px"
      border="1px solid"
      borderColor="neutrals.200"
      borderRadius="12px"
      alignItems="center"
      justifyContent="center"
      role="toolbar"
      aria-label="Text formatting toolbar"
    >
      <Flex gap="2px">
        <Button
          {...buttonProps}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          bg={editorState.isHeading1 ? 'neutrals.100' : 'transparent'}
          aria-label="Heading 1"
          aria-pressed={editorState.isHeading1}
        >
          <TextHOne weight="bold" />
        </Button>
        <Button
          {...buttonProps}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          bg={editorState.isHeading2 ? 'neutrals.100' : 'transparent'}
          aria-label="Heading 2"
          aria-pressed={editorState.isHeading2}
        >
          <TextHTwo weight="bold" />
        </Button>
        <Button
          {...buttonProps}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          bg={editorState.isHeading3 ? 'neutrals.100' : 'transparent'}
          aria-label="Heading 3"
          aria-pressed={editorState.isHeading3}
        >
          <TextHThree weight="bold" />
        </Button>

        <Button
          {...buttonProps}
          onClick={() => editor.chain().focus().toggleBold().run()}
          bg={editorState.isBold ? 'neutrals.100' : 'transparent'}
          aria-label="Bold"
          aria-pressed={editorState.isBold}
        >
          <TextB weight="bold" />
        </Button>
        <Button
          {...buttonProps}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          bg={editorState.isItalic ? 'neutrals.100' : 'transparent'}
          aria-label="Italic"
          aria-pressed={editorState.isItalic}
        >
          <TextItalic weight="bold" />
        </Button>

        <Button
          {...buttonProps}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          bg={editorState.isUnderline ? 'neutrals.100' : 'transparent'}
          aria-label="Underline"
          aria-pressed={editorState.isUnderline}
        >
          <TextUnderline weight="bold" />
        </Button>

        <Button
          {...buttonProps}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          bg={editorState.isStrike ? 'neutrals.100' : 'transparent'}
          aria-label="Strike through"
          aria-pressed={editorState.isStrike}
        >
          <TextStrikethrough weight="bold" />
        </Button>

        <Button
          {...buttonProps}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          bg={editorState.isBulletList ? 'neutrals.100' : 'transparent'}
          aria-label="Bullet list"
          aria-pressed={editorState.isBulletList}
        >
          <ListBullets weight="bold" />
        </Button>
        <Button
          {...buttonProps}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          bg={editorState.isOrderedList ? 'neutrals.100' : 'transparent'}
          aria-label="Numbered list"
          aria-pressed={editorState.isOrderedList}
        >
          <ListNumbers weight="bold" />
        </Button>
      </Flex>
    </Box>
  );
});

MenuBar.displayName = 'MenuBar';

const RichEditor = ({
  content,
  placeholder = '',
  onChange,
  readOnly = false,
  type,
  className,
  'aria-label': ariaLabel = 'Rich text editor'
}: RichEditorProps) => {
  const extensions = [
    Color.configure({ types: [TextStyle.name, ListItem.name] }),
    TextStyle,
    Document,
    Paragraph,
    Text,
    StarterKit.configure({
      bulletList: {
        keepMarks: true
      },
      orderedList: {
        keepMarks: true
      }
    }),
    Placeholder.configure({
      placeholder
    })
  ];

  const editor = useEditor({
    extensions,
    content,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: `${type || ''} ${className || ''}`.trim(),
        'aria-label': ariaLabel,
        role: 'textbox',
        'aria-multiline': 'true'
      }
    }
  });

  const handleUpdate = useCallback((): void => {
    if (editor && onChange) {
      onChange(editor.getHTML());
    }
  }, [editor, onChange]);

  editor.on('update', handleUpdate);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(convertNewlinesToHtml(content));
    }
  }, [content, editor]);

  const editorState = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => ({
      isHeading1: editor.isActive('heading', { level: 1 }),
      isHeading2: editor.isActive('heading', { level: 2 }),
      isHeading3: editor.isActive('heading', { level: 3 }),
      isBold: editor.isActive('bold'),
      isItalic: editor.isActive('italic'),
      isStrike: editor.isActive('strike'),
      isUnderline: editor.isActive('underline'),
      isBulletList: editor.isActive('bulletList'),
      isOrderedList: editor.isActive('orderedList')
    })
  });

  return (
    <Stack h="100%" gap="8px">
      {!readOnly && <MenuBar editor={editor} editorState={editorState} />}
      <EditorContent editor={editor} />
    </Stack>
  );
};

export default memo(RichEditor);
